import React, { useEffect, useState } from "react";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { Plus, Trash2, Sparkles } from "lucide-react";
import NumberInput from "@/components/NumberInput";

export default function StepCities({ q, setQ }) {
  const [cityQuery, setCityQuery] = useState("");
  const [suggestions, setSuggestions] = useState([]);
  const [templates, setTemplates] = useState([]);

  useEffect(() => {
    if (!q.destination.country) return;
    api.get("/catalog/cities", { params: { country: q.destination.country, q: cityQuery } })
       .then(({ data }) => setSuggestions(data));
  }, [cityQuery, q.destination.country]);

  useEffect(() => {
    if (!q.destination.country) { setTemplates([]); return; }
    api.get("/catalog/route-templates", { params: { country: q.destination.country } })
       .then(({ data }) => setTemplates(data || []))
       .catch(() => setTemplates([]));
  }, [q.destination.country]);

  const addCity = (name) => {
    const trimmed = (name || "").trim();
    if (!trimmed) return;
    setQ((p) => ({ ...p, cities: [...p.cities, { city: trimmed, nights: 2 }] }));
    setCityQuery("");
  };
  const updCity = (i, k, v) => setQ((p) => ({ ...p, cities: p.cities.map((c, idx) => idx===i ? { ...c, [k]: k==="nights"?Number(v):v } : c) }));
  const removeCity = (i) => setQ((p) => ({ ...p, cities: p.cities.filter((_, idx) => idx !== i) }));

  const applyTemplate = (tpl) => {
    setQ((p) => ({
      ...p,
      cities: tpl.cities.map((c) => ({ city: c.city, nights: c.nights })),
      package_title: p.package_title || tpl.name,
    }));
    toast.success(`Applied "${tpl.name}" template — ${tpl.cities.length} cities loaded.`);
  };

  if (!q.destination.country) return <div className="text-sm text-slate-500">Select a country in the previous step first.</div>;

  const trimmedQ = cityQuery.trim();
  const showCustom = trimmedQ.length >= 3 && !suggestions.some((s) => s.toLowerCase() === trimmedQ.toLowerCase());

  return (
    <>
      {templates.length > 0 && q.cities.length === 0 && (
        <div data-testid="route-templates-strip" className="mb-6 p-4 bg-emerald-50/50 border border-emerald-200 rounded">
          <div className="flex items-center gap-2 mb-3">
            <Sparkles size={14} className="text-emerald-700" />
            <span className="text-xs uppercase tracking-widest text-emerald-800 font-semibold">Quick-start templates for {q.destination.country}</span>
          </div>
          <div className="flex flex-wrap gap-2">
            {templates.map((tpl) => (
              <button
                key={tpl.id}
                data-testid={`route-tpl-${tpl.id}`}
                onClick={() => applyTemplate(tpl)}
                className="group text-left bg-white border border-emerald-200 hover:border-emerald-500 hover:shadow-sm rounded px-3 py-2 transition"
                title={tpl.tagline}
              >
                <div className="text-sm font-semibold text-slate-900 group-hover:text-emerald-700">{tpl.name}</div>
                <div className="text-[11px] text-slate-500">{tpl.cities.length} cities • {tpl.nights}N / {tpl.days}D</div>
              </button>
            ))}
          </div>
          <div className="text-[11px] text-slate-500 mt-3">Click a template to prefill cities + nights. You can still tweak them below.</div>
        </div>
      )}

      <div className="relative mb-6">
        <label className="block text-xs uppercase tracking-widest text-slate-500 mb-1.5">Add city (type 3+ letters)</label>
        <input data-testid="city-search" value={cityQuery} onChange={(e)=>setCityQuery(e.target.value)}
               onKeyDown={(e) => { if (e.key === "Enter" && trimmedQ.length >= 2) { e.preventDefault(); addCity(trimmedQ); } }}
               placeholder={`Cities in ${q.destination.country}`} className="w-full border border-border rounded px-3 py-2.5 text-sm" />
        {trimmedQ.length >= 3 && (suggestions.length > 0 || showCustom) && (
          <div className="absolute z-20 top-full mt-1 w-full bg-white border border-border rounded shadow-lg max-h-72 overflow-auto">
            {suggestions.map((c) => (
              <button key={c} data-testid={`city-opt-${c}`} onClick={()=>addCity(c)} className="block w-full text-left px-4 py-2 text-sm hover:bg-slate-50">{c}</button>
            ))}
            {showCustom && (
              <button
                data-testid="city-add-custom"
                onClick={() => addCity(trimmedQ)}
                className="block w-full text-left px-4 py-2 text-sm bg-emerald-50 hover:bg-emerald-100 text-emerald-800 border-t border-emerald-200"
              >
                <span className="inline-flex items-center gap-1"><Plus size={12} /> Add "<b>{trimmedQ}</b>" as custom city</span>
              </button>
            )}
          </div>
        )}
      </div>

      <div className="space-y-2">
        {q.cities.map((c, i) => (
          <div key={i} className="flex items-center gap-3 bg-slate-50 border border-border rounded p-3">
            <div className="flex-1 text-sm">{c.city}</div>
            <NumberInput testid={`city-nights-${i}`} value={c.nights} onChange={(v) => updCity(i, "nights", v)} min={1} max={365} placeholder="Nights" compact />
            <span className="text-xs text-slate-500">Nights</span>
            <button data-testid={`city-remove-${i}`} onClick={()=>removeCity(i)} className="p-1.5 hover:text-red-600"><Trash2 size={14} /></button>
          </div>
        ))}
        {q.cities.length === 0 && <div className="text-sm text-slate-400">No cities added yet.</div>}
      </div>

      {q.cities.length > 0 && (
        <div className="mt-6 bg-[#BBF7D0]/40 border border-[#BBF7D0] rounded px-4 py-3 text-sm text-slate-800 inline-block">
          <b>{q.total_nights} Nights</b> / <b>{q.total_days} Days</b>
        </div>
      )}
    </>
  );
}
